import { useCallback, useMemo, useState } from "react";
import { Box, Paper, Typography } from "@mui/material";
import { ErrorModal } from "@dexteel/mesf-core";
import {
	Group,
	Panel,
	Separator,
	useDefaultLayout,
} from "react-resizable-panels";
import { CenteredLazyLoading } from "@/controls/LazyLoading";
import { NotificationSnackBar } from "@/controls/snackbars/notification-snackbar";
import { DeleteConfirmModal } from "@/controls/modals/DeleteConfirmModal";
import type { ProductSize } from "../models/ProductSize";
import { PRODUCT_SIZE_INITIAL_VALUES } from "../models/ProductSize";
import {
	useActiveProductTypes,
	useProductSizes,
} from "../hooks/useProductSizes";
import {
	useDeleteProductSize,
	useDuplicateProductSize,
	useUpsertProductSize,
} from "../hooks/useProductSizeMutation";
import { ProductsTable } from "./ProductsTable";
import { ProductDetailViewer } from "./ProductDetailViewer";
import { ProductsToolbar } from "./ProductsToolbar";
import { DuplicateProductModal } from "./DuplicateProductModal";
import { SHOW_ONLY_ACTIVES_DEFAULT } from "@/controls/filters/ShowOnlyActivesCheckbox";
import {
	urlParam,
	useDropUnknownValue,
	useUrlState,
	useUrlText,
} from "@/shared/hooks/useUrlState";
import { useUrlSelection } from "@/shared/hooks/useUrlSelection";

const DEFAULT_LAYOUT = { left: 55, right: 45 };

const URL_SCHEMA = {
	search: urlParam.string(),
	onlyActives: urlParam.bool(SHOW_ONLY_ACTIVES_DEFAULT),
	productTypeId: urlParam.int(),
	productSizeId: urlParam.int(),
};

export const ProductsManager = () => {
	// Filters and the selected record, all in the query string.
	const [
		{ search, onlyActives, productTypeId, productSizeId },
		setFilters,
		{ dropValues },
	] = useUrlState(URL_SCHEMA);
	// The box types locally and the URL follows once typing stops.
	const [searchText, setSearchText] = useUrlText(search ?? "", (value) =>
		setFilters({ search: value || null }),
	);
	const [error, setError] = useState("");
	const [notification, setNotification] = useState("");
	const [deleteModalOpen, setDeleteModalOpen] = useState(false);
	const [productToDelete, setProductToDelete] = useState<ProductSize | null>(
		null,
	);
	const [productToDuplicate, setProductToDuplicate] =
		useState<ProductSize | null>(null);

	const { defaultLayout, onLayoutChange } = useDefaultLayout({
		id: "products-manager-v1",
		panelIds: ["left", "right"],
		storage: localStorage,
	});

	const { productSizes, isLoading, error: queryError } = useProductSizes();
	const { data: productTypes = [], isSuccess: typesLoaded } =
		useActiveProductTypes();

	// A link can name a type this page no longer offers.
	useDropUnknownValue(
		productTypeId,
		typesLoaded,
		productTypes.map((type) => type.Id),
		useCallback(() => dropValues("productTypeId"), [dropValues]),
	);

	const filteredProducts = useMemo(() => {
		const actives = onlyActives
			? productSizes.filter((p) => p.Active)
			: productSizes;
		const base =
			productTypeId === null
				? actives
				: actives.filter((p) => p.ProductTypeId === productTypeId);
		if (!searchText) return base;
		const s = searchText.toLowerCase();
		return base.filter(
			(p) =>
				p.Name?.toLowerCase().includes(s) ||
				p.ProductTypeName?.toLowerCase().includes(s) ||
				p.SectionCode?.toLowerCase().includes(s),
		);
	}, [productSizes, searchText, onlyActives, productTypeId]);

	// The id travels and the record is resolved from the list. A record being added has
	// no id yet, so it stays out of the URL.
	const selection = useUrlSelection<ProductSize>({
		id: productSizeId,
		setId: (value) => setFilters({ productSizeId: value }),
		dropId: () => dropValues("productSizeId"),
		rows: productSizes,
		ready: !isLoading,
		getId: (record) => record.Id,
	});
	const selectedProduct = selection.current;

	const { upsertProductSize, isUpserting } = useUpsertProductSize({
		onSuccess: (data) => {
			const isNew = selection.isNew;
			setNotification(
				isNew ? "Product created successfully" : "Product updated successfully",
			);
			if (data) {
				selection.adopt(data);
			}
		},
		onError: (err) => setError(err.message),
	});

	const { duplicateProductSize, isDuplicating } = useDuplicateProductSize({
		onSuccess: (product) => {
			setNotification("Product duplicated successfully");
			setProductToDuplicate(null);
			// The copy is not in the list yet: adopt holds it until the refetch brings it.
			selection.adopt(product);
		},
		onError: (err) => {
			setError(err.message);
			setProductToDuplicate(null);
		},
	});

	const { deleteProductSize, isDeleting } = useDeleteProductSize({
		onSuccess: () => {
			setNotification("Product deleted successfully");
			selection.clear();
			setDeleteModalOpen(false);
			setProductToDelete(null);
		},
		onError: (err) => setError(err.message),
	});

	const handleRowSelected = useCallback(
		(product: ProductSize | null) => {
			if (product) selection.select(product);
			else selection.clear();
		},
		[selection],
	);

	const handleDelete = useCallback((product: ProductSize) => {
		setProductToDelete(product);
		setDeleteModalOpen(true);
	}, []);

	const handleConfirmDelete = useCallback(() => {
		if (productToDelete?.Id) {
			deleteProductSize(productToDelete.Id);
		}
	}, [productToDelete, deleteProductSize]);

	const handleCancelDelete = useCallback(() => {
		setDeleteModalOpen(false);
		setProductToDelete(null);
	}, []);

	const handleAdd = useCallback(() => {
		selection.startNew({ ...PRODUCT_SIZE_INITIAL_VALUES });
	}, [selection]);

	const handleSave = useCallback(
		(product: ProductSize) => {
			upsertProductSize(product);
		},
		[upsertProductSize],
	);

	// Inline Active toggle from the grid (legacy parity). Full upsert with the
	// flipped flag — the grid row is a complete ProductSize, so every other field
	// is preserved.
	const handleToggleActive = useCallback(
		(product: ProductSize, active: boolean) => {
			upsertProductSize({ ...product, Active: active });
		},
		[upsertProductSize],
	);

	const handleClose = useCallback(() => {
		selection.clear();
	}, [selection]);

	if (queryError) {
		return <ErrorModal error={queryError.message} onHide={() => {}} />;
	}

	return (
		<Box
			sx={{
				width: "100%",
				height: "calc(100vh - 130px)",
				display: "flex",
				flexDirection: "column",
				gap: 1,
				p: 1,
			}}
		>
			<Typography variant="h5" sx={{ fontWeight: 600 }}>
				Products
			</Typography>

			<Paper elevation={3}>
				<ProductsToolbar
					search={searchText}
					onSearchChange={setSearchText}
					productTypeId={productTypeId}
					onProductTypeChange={(value) => setFilters({ productTypeId: value })}
					onlyActives={onlyActives}
					onOnlyActivesChange={(value) => setFilters({ onlyActives: value })}
					onAdd={handleAdd}
				/>
			</Paper>

			<Group
				id="products-manager-group"
				orientation="horizontal"
				defaultLayout={defaultLayout ?? DEFAULT_LAYOUT}
				onLayoutChange={onLayoutChange}
				style={{ flex: 1, width: "100%" }}
			>
				<Panel id="left" minSize={25} style={{ paddingBottom: 5 }}>
					<Box
						sx={{
							display: "flex",
							flexDirection: "column",
							height: "100%",
							pr: 1,
						}}
					>
						<Paper
							elevation={3}
							sx={{ flex: 1, width: "100%", overflow: "hidden" }}
						>
							{isLoading ? (
								<CenteredLazyLoading />
							) : (
								<ProductsTable
									products={filteredProducts}
									selectedProduct={selectedProduct?.Id ? selectedProduct : null}
									onRowSelected={handleRowSelected}
									onDelete={handleDelete}
									onToggleActive={handleToggleActive}
								/>
							)}
						</Paper>
					</Box>
				</Panel>

				<Separator
					style={{
						display: "flex",
						alignItems: "center",
						justifyContent: "center",
						cursor: "col-resize",
					}}
				>
					<Box
						sx={{
							width: 6,
							height: 40,
							borderRadius: 3,
							backgroundColor: "divider",
							"&:hover": { backgroundColor: "primary.main" },
						}}
					/>
				</Separator>

				<Panel id="right" minSize={30} style={{ paddingBottom: 5 }}>
					<Box sx={{ pl: 1, height: "100%" }}>
						<Paper elevation={3} sx={{ height: "100%", overflow: "hidden" }}>
							<ProductDetailViewer
								product={selectedProduct}
								existingProducts={productSizes}
								isSaving={isUpserting || isDeleting}
								onSave={handleSave}
								onDuplicate={setProductToDuplicate}
								onClose={handleClose}
							/>
						</Paper>
					</Box>
				</Panel>
			</Group>

			<DuplicateProductModal
				open={!!productToDuplicate}
				product={productToDuplicate}
				existingProducts={productSizes}
				isDuplicating={isDuplicating}
				onDuplicate={(name, sectionCode) =>
					productToDuplicate?.Id &&
					duplicateProductSize({
						id: productToDuplicate.Id,
						name,
						sectionCode,
					})
				}
				onCancel={() => setProductToDuplicate(null)}
			/>

			<DeleteConfirmModal
				open={deleteModalOpen}
				title="DELETE PRODUCT"
				itemName={productToDelete?.Name ?? ""}
				isDeleting={isDeleting}
				onConfirm={handleConfirmDelete}
				onCancel={handleCancelDelete}
			/>

			<NotificationSnackBar
				message={notification}
				onHide={() => setNotification("")}
			/>
			<ErrorModal error={error} onHide={() => setError("")} />
		</Box>
	);
};
